import { useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Section, Container } from '../../ui';
import styles from './BusinessModel.module.css';

export function BusinessModel() {
  const { t } = useTranslation('solutions');
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const hasAnimated = useRef(false);

  const blocks = t('businessModel.blocks', { returnObjects: true }) as {
    title: string; text: string; tagline: string; details: string[];
  }[];
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated.current) {
          hasAnimated.current = true;
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <Section variant="gray" id="business-model">
      <Container>
        <div ref={ref} className={`${styles.wrapper} ${isVisible ? styles.visible : ''}`}>
          <div className={styles.header}>
            <div className={styles.badgeSpacer} />
            <h1 className={styles.heading}>{t('businessModel.heading')}</h1>
            <p className={styles.subtitle}>{t('businessModel.subtitle')}</p>
          </div>

          <div className={styles.blocks}>
            {blocks.map((block, i) => (
              <div
                key={i}
                className={`${styles.block} ${i % 2 !== 0 ? styles.blockReverse : ''}`}
                style={{ '--block-index': i } as React.CSSProperties}
              >
                <div className={styles.blockVisual}>
                  <span className={styles.zero}>0</span>
                  <span className={styles.zeroLabel}>{block.title}</span>
                </div>
                <div className={styles.blockContent}>
                  <h3 className={styles.blockTitle}>{block.title}</h3>
                  <p className={styles.blockText}>{block.text}</p>
                  <ul className={styles.detailList}>
                    {block.details.map((d, j) => (
                      <li key={j} className={styles.detailItem}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <circle cx="12" cy="12" r="10" />
                          <path d="m9 12 2 2 4-4" />
                        </svg>
                        {d}
                      </li>
                    ))}
                  </ul>
                  <span className={styles.tagline}>{block.tagline}</span>
                </div>
              </div>
            ))}
          </div>

          <p className={styles.hook}>{t('businessModel.hook')}</p>

          <div className={styles.gfkCitation}>
            <blockquote className={styles.gfkQuote}>
              "{t('businessModel.gfkQuote')}"
            </blockquote>
            <cite className={styles.gfkSource}>— {t('businessModel.gfkSource')}</cite>
          </div>
        </div>
      </Container>
    </Section>
  );
}
